import {Injectable, Injector} from '@angular/core';
import {DialogFormInput, LangService, LoaderService, NavigateService} from 'ngx-satoris';
import {
  ActionState,
  OperationType,
  PassportType, PrintReceiptData,
  PurposeOfVisit,
  Request, RequestCashType,
  RequestMNTAType,
  RequestState,
  RequestSubState,
  RequestTheme,
  RequestWithDuplicata
} from '../models/request';
import {ApiService} from './api.service';
import {Document, DocumentType, PriceOverride, UserPermission} from '../models/user';
import {CartService, FormApplication} from './cart.service';
import {FormField, VisaFN, VisaFormConfig, VisaFormName} from '../models/forms';
import {
  differenceInHours,
  endOfDay,
  isAfter,
  isValid,
  addMilliseconds,
  differenceInDays
} from 'date-fns';
import {environment} from 'src/environments/environment';
import {Person} from '../models/person';
import {Subscription} from 'rxjs';
import {flattenObject} from '../utils/flattenObject';
import {PlatformEvent, PlatformEventType} from '../models/information';
import {QueueService} from './queue.service';
import * as uuid from 'uuid';
import {PrintService} from './print.service';
import {getCountries, getExtensionType, getRequestData, getStatusTheme, getDocumentTypeIsVisaExtC, documentTypeIsVisaExtAB, documentTypeIsVisaExtC} from '../models/default-filter-strategy';
import {generateQrOffline} from '../utils/generateQr';
import {AllDocumentsPrintType, DocsPrintType} from '../models/print';
import {ZimswitchComponent} from 'src/app/components/zimswitch/zimswitch.component';
import {ScanDocWebcamComponent} from 'src/app/components/scan-doc-webcam/scan-doc-webcam.component';
import {MatDialog} from '@angular/material/dialog';
import {Validators} from '@angular/forms';
import {FingerprintService} from './fingerprint.service';
import {getDocumentTypeIsExit} from '../models/document-type-helpers';
declare const window: any;

@Injectable({
  providedIn: 'root'
})
export class RequestService {
  entryPoints: string[] = [];
  pendingNoFeeProof: string;

  allMyPayments: RequestWithDuplicata[];
  allExtensions: RequestWithDuplicata[] = [];
  allBatchIds: string[];
  allMyApplications: {batchId: string, requests: RequestWithDuplicata[]}[];
  invalidatePersons: Person[] = [];
  inPrintProcess = false;

  private signOutSubscription: Subscription;
  private printSubscription: Subscription;

  constructor(public cart: CartService,
              public lang: LangService,
              public api: ApiService,
              private loader: LoaderService,
              private nav: NavigateService,
              private queue: QueueService,
              private injector: Injector,
              private dialog: MatDialog,
              private fingerprint: FingerprintService) {
    this.signOutSubscription = this.api.signOutObservable.subscribe({
      next: () => {
        this.allMyPayments = undefined;
        this.allExtensions = undefined;
        this.allBatchIds = undefined;
        this.allMyApplications = undefined;
        this.invalidatePersons = undefined;
      }
    });
  }

  ngOnDestroy() {
    this.signOutSubscription.unsubscribe();
    this.printSubscription?.unsubscribe();
  }

  pay(isPayOnline = false, application?: FormApplication) {
    return this.payApplication(isPayOnline, application).then((res: any) => {
      if(this.api.userRole.isWorker) {
        if(res && !res.queueId) {
          const paymentId = res && typeof res === 'object' && Array.isArray(res.ids) ? res.ids[0] : res;
          this.nav.to('admin-request-select/' + paymentId, undefined, {queryParams: {registerSession: true}});
          setTimeout(() => {
            this.cart.deleteSavedApplication(false);
          });
        } else if(res.queueId) {
          //Offline/Timeout has been detected coming from the queue
          this.cart.currentApplication.queueId = res.queueId;
          this.cart.currentApplication.offlineParentMethodName = 'registerMNTAPayment';

          this.nav.to('admin-request-select/' + null, undefined, {queryParams: {offlineApplication: JSON.stringify(this.cart.currentApplication)}});
          setTimeout(() => {
            this.cart.deleteSavedApplication(false);
          });
        } else {
          this.loader.loading(true, {type: 'error', message: 'global.error'});
          return Promise.reject('global.error');
        }
      } else if(res && this.api.env.formHasBookTimeSlotStep) {
        return res;
      } else if(res && res.body?.checkoutUrl) {
        window.location.href = res.body.checkoutUrl;
      } else if(res) {
        if(this.api.userRole.isKiosk) {
          this.nav.to('success-kiosk');
        } else {
          this.nav.to('payment-success');
        }
        setTimeout(() => {
          this.cart.deleteSavedApplication(false);
        });
      } else if(!application) {
        this.loader.loading(true, {type: 'valid', message: 'revision.success'});
        this.nav.to('dashboard');
      }
      return res; // Return res to make it available in the next then block
    }).then((res) => {
      this.invalidatePersons = this.cart.currentApplication.requests.map(request => request.person);
      return res; // Return res to propagate it to the caller
    });
  }

  payApplication(isPayOnline = false, application?: FormApplication): Promise<any> {
    if(!application && this.cart.currentApplication) {
      application = this.cart.currentApplication;
    }

    return new Promise((resolve, reject) => {
      const payments: { request_id: string, person_id: number, serialized: string }[] = [];
      (application || this.cart.currentApplication).requests.forEach((request: Request) => {
        const data = getRequestData(this, request, application);
        Object.keys(data).forEach(key => {
          if(data[key] === undefined) {
            delete data[key];
          }
        });
        payments.push({
          request_id: request.visaType.id,
          person_id: request.person.id ? Number(request.person.id) : undefined,
          serialized: JSON.stringify(data)
        });
      });

      let previousBatchId: any = undefined;
      if(this.api.userRole.isCustomer && !application.isExtend) {
        previousBatchId = application.isRevision ? application.travelName : (this.allBatchIds?.find(b => b === application.travelName) || undefined);
      }

      const onExtensionTreated = ({applyPenalty, paymentReference, paymentMode, paymentCurrency}: {
        applyPenalty: boolean,
        paymentReference: string,
        paymentMode: string,
        paymentCurrency: string
      }) => {
        payments.forEach(payment => {
          const originalData = JSON.parse(payment.serialized);
          if(paymentReference && paymentMode && paymentCurrency) {
            originalData.PaymentReference = paymentReference;
            originalData.PaymentMode = paymentMode;
            originalData.PaymentCurrency = paymentCurrency;
          }
          if(this.pendingNoFeeProof) {
            if(!originalData.Documents) originalData.Documents = {};
            originalData.Documents.NoFeeProof = this.pendingNoFeeProof;
            this.pendingNoFeeProof = undefined;
          }
          payment.serialized = JSON.stringify(originalData);
        });
        this.loader.loading(true);
        if(!this.api.env.formHasTravelNameStep && !application.travelName) {
          application.travelName = this.cart.getUniqueReference(this.allMyPayments?.map(p => p.batchId) || []);
        }

        const nonDocumentExtra = applyPenalty ? 'overstay' : undefined;
        if(this.api.userRole.isWorker && this.api.hasPerm(UserPermission.ALLOW_CREATE) && this.api.hasPerm(UserPermission.ALLOW_CONSUME)) {
          this.queue.addToQueue('registerMNTAPayment',
            [payments, application?.travelName, previousBatchId, false, RequestMNTAType.IVERI, nonDocumentExtra, environment.appVersion.join('.'), new Date().getTime()],
            5000,
            undefined,
            undefined,
            true).then((res: any) => {
            this.loader.loading(false);
            if(res.queueId) {
              resolve({queueId: res.queueId});
            } else {
              resolve(this.processPaymentResponse(application.paymentMethod, res as {ids: string[], body: any}));
            }
          }).catch(err => {
            this.loader.loading(false);
            reject(err);
          });

        } else if(application.isRevision) {
          this.api.customTimeout = 300000;
          this.api.updatePayment(application.requestId, undefined, undefined, payments[0].serialized, undefined, undefined).then(() => {
            this.loader.loading(false);
            this.api.customTimeout = undefined;
            this.invalidatePersons = application.requests.map(request => request.person);
            resolve(undefined);
          }).catch((err) => {
            this.loader.loading(false);
            this.api.customTimeout = undefined;
            reject(err);
          });
        } else {
          if(this.api.userRole.isCustomer || this.api.userRole.isKiosk) this.api.customTimeout = 300000;

          if(application.paymentMethod === RequestMNTAType.PAYPAL) {
            this.api.registerMNTAPayment(payments, application.travelName, previousBatchId, isPayOnline, application.paymentMethod, nonDocumentExtra).then(res => {
              if(res) {
                this.loader.loading(false);
                resolve(res);
              } else {
                reject('global.error');
              }
            }).catch(err => {
              this.loader.loading(true, {type: 'error', message: err});
              this.api.customTimeout = undefined;
              reject(err);
            });
          } else if(!application.paymentMethod || application.paymentMethod === RequestMNTAType.IVERI || application.paymentMethod === RequestMNTAType.ZIMSWITCH) {
            this.api.registerMNTAPayment(payments, application.travelName, previousBatchId, isPayOnline, application.paymentMethod, nonDocumentExtra).then(res => {
              this.api.customTimeout = undefined;
              if(this.api.userRole.isCustomer) {
                this.allBatchIds.push(application.travelName);
              }
              if(this.api.env.formHasBookTimeSlotStep && res) {
                this.loader.loading(false);
                return resolve(res);
              }
              resolve(this.processPaymentResponse(application.paymentMethod, res));
              this.loader.loading(false);
            }).catch(err => {
              this.loader.loading(false);
              this.api.customTimeout = undefined;
              reject(err);
            });
          }
        }
      };
      this.payExtension(application).then(onExtensionTreated, onExtensionTreated);
    });
  }

  isFieldInSchema(fieldName: string, schemaId: string): boolean {
    const schema = this.api.schemaSync(schemaId);
    if(schema) {
      try {
        return schema.properties[fieldName] !== undefined;
      } catch (e) {}
    }
    return true;
  }


  processPaymentResponse(type: RequestMNTAType, res: {ids?: string[], body: any}, application?: FormApplication): string {
    this.cart.currentApplication.submitted = true;
    if(res.body && this.api.userRole.isCustomer) {
      this.cart.saveCurrentApplication(false);
      if(type === RequestMNTAType.IVERI) {
        const form = document.createElement('form');
        form.method = 'POST';
        form.action = 'https://' + this.api.userInfo.server.mnta.host + '/Lite/Authorise.aspx';
        Object.keys(res.body).forEach(key => {
          const input = document.createElement('input');
          input.type = 'hidden';
          input.name = key;
          input.value = res.body[key];
          form.appendChild(input);
        });
        document.body.appendChild(form);
        form.submit();
      } else if(type === RequestMNTAType.ZIMSWITCH) {
        /* TEST CARD
          1234560100000545
          12/50
          060
        */
        this.dialog.open(ZimswitchComponent, {
          width: '100svw',
          height: '100svh',
          maxWidth: '100svw',
          maxHeight: '100svh',
          panelClass: 'zimSwitchPanel',
          autoFocus: true,
          disableClose: true,
          data: res.body
        });
      }
    } else {
      this.invalidatePersons = (application || this.cart.currentApplication).requests.map(request => request.person);
      return res.ids[0];
    }
  }

  getStatusTheme(request: RequestWithDuplicata): RequestTheme {
    return getStatusTheme(request, this);
  }

  getHasPendingPayment(requests: Request[] | RequestWithDuplicata[]): boolean {
    return requests.some(request => request.state === RequestState.PROPOSED);
  }

  isDelayed(request: RequestWithDuplicata): boolean {
    if((request.state !== RequestState.PAID_NOT_CONFIRMED && request.state !== RequestState.DELAY_PAY_NOT_CONFIRMED)) {
      return false;
    }
    return new Date().getTime() - new Date(request.createdAt).getTime() > 604800000;
  }

  getHighestSeverityTheme = (app: {batchId: string; requests: RequestWithDuplicata[]}, request?: Request | RequestWithDuplicata): RequestTheme => {
    let highestSeverity: RequestTheme;
    if(request) {
      const requestTheme = this.getStatusTheme(request as RequestWithDuplicata);
      const extensionsThemes = request.extensions?.map(ext => this.getStatusTheme(ext as RequestWithDuplicata))[0];
      highestSeverity = extensionsThemes && extensionsThemes.severity > requestTheme.severity ? extensionsThemes : requestTheme;

    } else {
      const requestThemes = app.requests.map(req => this.getStatusTheme(req as RequestWithDuplicata));
      const requestsHighestSeverity = requestThemes.length && requestThemes.reduce((prev, curr) => prev.severity > curr.severity ? prev : curr);
      const extensionsThemes = app.requests.map(req => req.extensions?.map(ext => this.getStatusTheme(ext as RequestWithDuplicata))[0])?.filter(ext => ext !== undefined);
      const extensionsHighestSeverity = extensionsThemes.length && extensionsThemes.reduce((prev, curr) => prev.severity > curr.severity ? prev : curr);
      highestSeverity = extensionsHighestSeverity && extensionsHighestSeverity.severity > requestsHighestSeverity.severity ? extensionsHighestSeverity : requestsHighestSeverity;
    }
    return highestSeverity;
  };

  getLatestModificationDate(app: { batchId: string; requests: Request[] }): string {
    return app.requests.reduce((prev, curr) => prev.updatedOn > curr.updatedOn ? prev : curr).updatedOn;
  }

  getBatch(batchId: string) {
    return this.allMyApplications.find(app => app.batchId === batchId);
  }

  isDocumentType(document: DocumentType): boolean {
    const documentType = this.api.userInfo.server.paymentRequests.find(d => d.id === document);
    return documentType.customer;
  }

  sometimesPaidOnline(document: DocumentType): boolean {
    const documentType = this.api.userInfo.server.paymentRequests.find(d => d.id === document);
    return documentType.paidOnline !== false;
  }

  isPaidOnline(document: DocumentType, unserialized: any, request?: Request): boolean {
    const documentType = this.api.userInfo.server.paymentRequests.find(d => d.id === document);
    if(documentType.paidOnline === true) return true;
    else if(documentType.paidOnline === false) return false;
    else {
      const portId = this.getFieldValue(VisaFormName.PERSONAL_DETAILS, VisaFN.REGISTRATION_PORT_ID, request?.formConfig) || this.getFieldValue(VisaFormName.MISCELLANEOUS_DETAILS, VisaFN.REGISTRATION_PORT_ID, request?.formConfig);
      if(portId) {
        return eval(documentType.paidOnline)({RegistrationPortId: portId});
      } else {
        return eval(documentType.paidOnline)(unserialized);
      }
    }
  }

  isNextCheckout(request: RequestWithDuplicata): boolean {
    const sortedEvents = request?.events?.sort((a, b) => new Date(b.updatedOn).getTime() - new Date(a.updatedOn).getTime());
    const lastRelevantEvent = sortedEvents?.find(event => event.indexed.Operation === OperationType.IN || event.indexed.Operation === OperationType.OUT);
    return lastRelevantEvent?.indexed?.Operation === OperationType.IN;
  }

  detectCheckRefusal(request: RequestWithDuplicata): boolean | OperationType {
    if(request?.events?.length > 0) {
      const lastEvent = request.events[request.events.length - 1];
      const firstEvent = request.events[0];
      if(firstEvent?.indexed?.Operation === OperationType.REFUSEIN || lastEvent?.indexed?.Operation === OperationType.REFUSEIN) {
        return OperationType.REFUSEIN;
      } else if(firstEvent?.indexed?.Operation === OperationType.REFUSEOUT || lastEvent?.indexed?.Operation === OperationType.REFUSEOUT) {
        return OperationType.REFUSEOUT;
      }
    }
    return false;
  }

  getDeportedStatus(request: RequestWithDuplicata): PlatformEvent | null {
    if(request?.events?.length > 0) {
      const inReturnedEvent = request.events.find(event => event.indexed?.Operation === OperationType.INRETURNED);
      if(inReturnedEvent) {
        return inReturnedEvent;
      }
    }
    return null;
  }

  getFieldValue(formName: VisaFormName, fieldName: VisaFN, config: VisaFormConfig): any {
    const c = config;
    if(!c[formName]?.fields) return undefined;
    return (typeof c[formName].fields?.find((field: FormField) => field.name === fieldName)?.value === 'string' ? c[formName].fields?.find((field: FormField) => field.name === fieldName)?.value : c[formName].fields?.find((field: FormField) => field.name === fieldName)?.value) || undefined;
  }

  getField(formName: VisaFormName, fieldName: string, config: VisaFormConfig): FormField {
    const c = config;
    return c[formName]?.fields?.find((field: FormField) => field.name === fieldName);
  }

  getNationalities(withRefugees: boolean, previousVisaRequiredOn = false): {value: string, name: string}[] {
    const noDeclarations: {value: string, name: string}[] = [];
    const sorted = getCountries(previousVisaRequiredOn)?.filter(n => withRefugees? true : !n.value.startsWith('XX'))
      .sort((a, b) => {
        if(environment.nationalityNoDeclaration.includes(a.value)) noDeclarations.push(a);
        if(environment.nationalityNoDeclaration.includes(b.value)) noDeclarations.push(b);
        return a.name.localeCompare(b.name);
      });
    if(noDeclarations.length) {
      noDeclarations.sort((a, b) => a.name.localeCompare(b.name));
      noDeclarations.forEach((nd) => {
        const index = sorted.findIndex((n) => n.value === nd.value);
        if(index > -1) {
          sorted.splice(index, 1);
          sorted.unshift(nd);
        }
      });
    }
    return sorted;
  }

  getTranslatedNationalities(withRefugees: boolean, catCOnly = false): {value: string, name: string}[] {
    const nationalities = this.getNationalities(withRefugees, catCOnly);

    return nationalities.map(nationality => ({
      value: nationality.value,
      name: this.lang.transform(`DYN.${nationality.value}`) || nationality.name
    }));
  }

  convertCurrency(sourceCurrency: string, amount: number, targetCurrency: string) {
    const sourceRate = this.api.userInfo.rates.rates[sourceCurrency];
    const targetRate = this.api.userInfo.rates.rates[targetCurrency];
    if(!sourceRate || !targetRate) {
      return null;
    }
    const convertedAmount = (amount / sourceRate * targetRate).toFixed(2);
    return {
      currency: targetCurrency,
      amount: convertedAmount
    };
  }

  getFundsDisplay(funds: any, currency: string): string {
    if(!funds || !currency) return '';
    let result = `${funds} ${currency}`;
    if(currency !== 'USD') {
      const converted = this.convertCurrency(currency, funds, 'USD');
      if(converted) {
        result += ` (${converted.amount} ${converted.currency})`;
      }
    }
    return result;
  }

  isUsageDateValid(date: string, type: 'usageAfter' | 'usageUntil') {
    if(type === 'usageAfter') {
      return new Date() >= new Date(date);
    } else {
      return new Date() <= new Date(date);
    }
  }

  isLocalUsageDateValid(date: any): boolean {
    if(!date) {
      return true;
    }
    const parsedDate = new Date(date);
    if(!isValid(parsedDate)) {
      return false;
    }
    return isAfter(endOfDay(parsedDate), endOfDay(new Date()));
  }

  isBlacklisted(request: RequestWithDuplicata): boolean {
    return ['blacklist', 'black list', 'black-list', 'blacklisted', 'black-listed'].some(blacklist => request?.refusalReason?.toLowerCase().includes(blacklist));
  }

  isExtension(request: Request): boolean {
    return request && (documentTypeIsVisaExtAB(request.operationId) || documentTypeIsVisaExtC(request.operationId));
  }

  checkIfNoTimeslot() {
    if(this.api.userRole.isCustomer) {
      const openDialog = (req: RequestWithDuplicata) => {
        this.loader.loading(true, {type: 'warn', message: 'ask.noTimeSlotBooked',  btnLabel: 'yes',  custom: {icon: 'clock', closeBtnLabel: 'no'}}).then((done) => {
          if(done) this.nav.to('request-account/' + req.id);
        });
      };

      this.allMyApplications.forEach((app) => {
        app.requests.forEach((req: RequestWithDuplicata) => {
          if(!req.timeslot && (req.state === RequestState.PAID || req.state === RequestState.DELAY_PAY)) {
            openDialog(req);
            return;
          }
        });
      });
    }
  }

  checkIfPendingWaiting() {
    if(this.api.userRole.isCustomer) {
      const openDialog = (req: RequestWithDuplicata) => {
        this.loader.loading(true, {type: 'warn', message: 'ask.pendingRequest', btnLabel: 'yes', custom: {icon: 'clock', closeBtnLabel: 'no'}}).then((done) => {
          if(done) this.nav.to('request-account/' + req.id);
        });
      };
      this.allMyApplications.forEach((app) => {
        app.requests.forEach((req: RequestWithDuplicata) => {
          if(req.state === RequestState.PENDING) {
            openDialog(req);
            return;
          }
          if(req.extensions.length) {
            req.extensions.forEach((ext) => {
              if(ext.state === RequestState.PENDING) {
                openDialog(req);
                return;
              }
            });
          }
        });
      });
    }
  }

  calculateLocalUntil(documentId: DocumentType, passportType: PassportType | string, nationality: string, purposeOfVisit: PurposeOfVisit, useMaximum = false): number | undefined {
    if(passportType === undefined) {
      return undefined;
    }
    try {
      const propertyName = useMaximum ? 'localUsageUntilFromNowMillis' : 'defaultLocalUsageUntilFromNowMillis';
      const localUntilFromServerString = this.api.userInfo.server.paymentRequests.find(doc => doc.id === documentId)?.[propertyName];
      if(localUntilFromServerString === undefined) {
        return undefined;
      }
      const localUntilFromServer = eval(localUntilFromServerString);
      const unserialized = {PassportType: passportType, Nationality: nationality, Documents: {PurposeOfVisit: purposeOfVisit}};
      return localUntilFromServer(unserialized);
    } catch (e) {
      return undefined;
    }
  }

  calculateMaxLocalUntil(documentId: DocumentType, passportType: PassportType | string, nationality: string, purposeOfVisit: PurposeOfVisit): number | undefined {
    return this.calculateLocalUntil(documentId, passportType, nationality, purposeOfVisit, true);
  }

  isOverstay(request: RequestWithDuplicata): boolean {
    return request.localUsageUntilValid === false &&
      [RequestState.PAID, RequestState.DELAY_PAY, RequestState.USED].includes(request.state) &&
      request.subState === RequestSubState.CHECKED_IN &&
      request.document?.multiUsage;
  }

  isOverstayOut(request: RequestWithDuplicata): boolean {
    return request.localUsageUntilValid === false &&
      [RequestState.PAID, RequestState.DELAY_PAY, RequestState.USED].includes(request.state) &&
      request.subState === RequestSubState.CHECKED_OUT &&
      request.document?.multiUsage;
  }

  getNameRequest(request: Request): string {
    const {operationId, id, metadata} = request;
    if(documentTypeIsVisaExtAB(operationId) || documentTypeIsVisaExtC(operationId)) {
      const linkedRequest = this.allMyPayments.find(payment => {
        if(payment.extensions && payment.extensions.length) {
          return payment.extensions.find(extension => extension.id === id);
        } else {
          return false;
        }
      });
      if(linkedRequest) {
        const {firstName, lastName} = linkedRequest.metadata;
        return `${firstName} ${lastName}`;
      }
    } else if(metadata?.firstName && metadata?.lastName) {
      return `${metadata.firstName} ${metadata.lastName}`;
    }
    return '';
  }


  isSubjectToUntil(request: Request): boolean {
    const dataActualDocument = this.api.userInfo.server.paymentRequests.find(document => document.id === request?.operationId);
    if(!dataActualDocument || !dataActualDocument.usageUntilFromNowMillis) return false;
    if(documentTypeIsVisaExtC(request.operationId) || documentTypeIsVisaExtAB(request.operationId)) return false;

    const context = {...(request.serialized || {})};
    try {
      const usageUntilFromNow = eval(dataActualDocument.usageUntilFromNowMillis);
      return !!usageUntilFromNow(context);
    } catch (e) {
      return false;
    }
  }

  hasNoAwaitingExtension(request: Request): boolean {
    if(!request?.extensions) return true;
    return request?.extensions?.every((request: Request) => request.state !== RequestState.PENDING && request.state !== RequestState.PAID_NOT_CONFIRMED && request.state !== RequestState.DELAY_PAY_NOT_CONFIRMED && request.state !== RequestState.PROPOSED);
  }

  startExtend(request: Request, metadata?: any) {
    this.loader.loading(true);
    const extendVisaFormConfig: VisaFormConfig = JSON.parse(JSON.stringify(this.cart.getOriginalForm(getDocumentTypeIsVisaExtC() as DocumentType | undefined)));
    const meta = metadata || flattenObject(request.serialized);

    const extendApplication: FormApplication = {
      travelName: undefined,
      plannedEntry: meta.ProvisionnalEntryPort,
      requests: [{
        formConfig: extendVisaFormConfig,
        nationality: meta.Nationality,
        passportType: meta.PassportType,
        visaType: getExtensionType(this.api.userInfo.server.paymentRequests, meta),
        urgent: meta.Urgent,
        person: {
          firstName: meta.FirstName || this.api.listPersons?.find((person: Person) => person.id === request.person_id)?.firstName || '',
          lastName: meta.LastName || this.api.listPersons?.find((person: Person) => person.id === request.person_id)?.lastName || '',
          nationalNumber: '',
          batchIds: [],
          passportNumber: '',
          id: metadata ? undefined : Number(request.person_id),
          linkType: null,
          otherLinkType: '',
          birthDate: '',
          sex: request.metadata.Gender,
          birthPlaceCity: '',
          birthPlaceCountry: '',
          nationality: request.metadata.Nationality
        }
      }],
      parentRequest: request as RequestWithDuplicata,
      visaFeesAndTerms: false,
      isExtend: true,
      requestId: request.id,
      externalId: request.externalId,
      hqRef: request.internalReference
    };

    this.loader.loading(true, {type: 'info', btnLabel: 'continue', custom: {icon: 'calendar-plus', innerHtml: this.lang.transform('extend.start')}}).then((done ) => {
      if(done) {
        this.cart.startApplication(false, extendApplication, extendVisaFormConfig);
      }
    });
  }

  requestHasBeenCorrected(payment: RequestWithDuplicata) {
    if(!payment.events || payment.events?.length < 2) return false;
    const events = payment.events?.sort((a, b) => new Date(b.updatedOn).getTime() - new Date(a.updatedOn).getTime()).filter(event => event?.type === PlatformEventType.PAYMENT_SUSPEND_CHANGE || event?.type === PlatformEventType.PAYMENT_VALIDATED || event?.type === PlatformEventType.PAYMENT_REFUSED  || (event?.type === PlatformEventType.PAYMENT_UPDATED && !event?.place_id));
    return events[1]?.type === PlatformEventType.PAYMENT_SUSPEND_CHANGE && events[0].type === PlatformEventType.PAYMENT_UPDATED;
  }

  normalizeName(name: string): string {
    if(!name) return undefined;
    return name
      .normalize('NFD') // Décompose les caractères accentués en lettres de base + accents
      .replace(/[\u0300-\u036f]/g, '') // Supprime les accents
      .toUpperCase() // Convertit en majuscules
      .replace(/[^A-Z' -]/g, ''); // Conserve uniquement les lettres, apostrophes, tirets et espaces
  }

  getPersonsWithReviewStatus(persons: Person[]) {
    if(!persons.length) return [];
    if(!persons.some(person => person.batchIds)) {
      this.getBatchIds(persons);
    }
    return persons.map((person) => {
      const shouldDisable = (predicate: (request: RequestWithDuplicata) => boolean) => person?.batchIds?.some((batchId) => {
        const apps = this.allMyApplications.filter(app => app.batchId === batchId);
        return apps && apps.some((app) => app.requests && app.requests.some((request) => request.person_id === person.id && predicate(request)));
      }) || false;
      const isOlderThanOneDay = (request: RequestWithDuplicata) => differenceInHours(new Date(), new Date(request.createdAt)) >= 24;
      return {
        ...person,
        disabledBecauseReview: shouldDisable((request) => this.isUnderReview(request)),
        disabledBecauseDraft: shouldDisable((request) => request.state === RequestState.PROPOSED && !isOlderThanOneDay(request)),
        disabledBecauseDraftWarn: shouldDisable((request) => request.state === RequestState.PROPOSED && isOlderThanOneDay(request))
      };
    });
  }

  getBatchIds(persons: Person[]) {
    persons.forEach((person: Person) => {
      person.batchIds = person.batchIds || [];
      this.allMyPayments.forEach((payment: Request) => {
        if(payment.person_id === person.id && payment.batchId !== null) {
          person.batchIds.push(payment.batchId);
        }
      });
    });
  }

  filterRequestsByActionState(requests: RequestWithDuplicata[], actionState: ActionState[]): RequestWithDuplicata[] {
    return requests.filter(request => actionState.includes(this.getStatusTheme(request).actionState));
  }

  isUnderReview(request: RequestWithDuplicata): boolean {
    return (request.state === RequestState.PAID_NOT_CONFIRMED || request.state === RequestState.DELAY_PAY_NOT_CONFIRMED) &&
    (request.subState === RequestSubState.REVISIONS_0 ||
     request.subState === RequestSubState.REVISIONS_1 ||
     request.subState === RequestSubState.REVISIONS_2 ||
     request.subState === RequestSubState.REVISIONS_3_MORE);
  }

  usageUntilFromNowDays(request: Request): number {
    const calculateDays = (usageUntilFromNowMillis: string | undefined, context: any) => {
      if(!usageUntilFromNowMillis) return 0;
      try {
        const fn = eval(usageUntilFromNowMillis);
        const argCount = fn.length;
        let milliseconds;
        if(argCount === 2) {
          milliseconds = fn(context, this.api.userInfo || {});
        } else {
          milliseconds = fn(context);
        }
        return differenceInDays(addMilliseconds(new Date(0), milliseconds), new Date(0));
      } catch (e) {
        return 0;
      }
    };

    const context = {
      ...(request.metadata || {}),
      ...(request.serialized || {}),
      Documents: request.metadata?.Documents || request.serialized?.Documents || undefined
    };

    if(request.document) {
      return calculateDays((request.document as Document).usageUntilFromNowMillis, context);
    }
    if(request.visaType?.usageUntilFromNowMillis) {
      return calculateDays(request.visaType.usageUntilFromNowMillis, context);
    }
    return 0;
  }

  getRatesText(documentType: DocumentType, nationality: string, customAmount?: number, ignorePriceOverride = false) {
    const serverRates = this.api.userInfo.rates.rates;
    const authorizedCurrencies = this.api.env.supportedCurrencies;
    const availableRates: {currency: string, price: number}[] = Object.keys(serverRates).filter(currency => authorizedCurrencies.includes(currency)).map(currency => (
      {currency, price: this.roundPrice((customAmount) * serverRates[currency], currency, documentType, nationality, ignorePriceOverride)}
    ));
    return '<b></br></br>'+availableRates.map(rate => `<h2>${rate.currency} ${rate.price}</h2>`).join('')+'</b>';
  }

  roundPrice(priceCents: number, currency: string, documentType: DocumentType, nationality: string, ignorePriceOverride = false): number {
    const price = priceCents / 100;
    const now = new Date();

    let bestMatchOverride: PriceOverride;
    let generalMatchCandidate: PriceOverride;

    if(this.api.userInfo.priceOverrides && Array.isArray(this.api.userInfo.priceOverrides) && !ignorePriceOverride) {
      for(const override of this.api.userInfo.priceOverrides) {
        // Ensure basic conditions (documentType and currency) are met
        if(override.id === documentType && override.currency === currency) {
          // Date validation
          const validFromDate = override.validFrom ? new Date(override.validFrom) : null;
          // If validUntil is null, undefined, or an empty string, it means no expiration date.
          const validUntilDate = override.validUntil ? new Date(override.validUntil) : null;

          const isCurrentlyValid =
            validFromDate && validFromDate <= now &&
            (!validUntilDate || now < validUntilDate);

          if(isCurrentlyValid) {
            // Priority 1: Exact nationality match
            if(override.nationality === nationality) {
              bestMatchOverride = override;
              break; // Found the highest priority match, no need to search further
            }
            // Priority 2: General match (nationality is null, undefined, or empty string)
            else if((!override.nationality || override.nationality.trim() === '') && !generalMatchCandidate) {
              generalMatchCandidate = override;
            }
          }
        }
      }
    }

    // Determine the final override to use: specific match takes precedence
    const finalPriceOverride = bestMatchOverride || generalMatchCandidate;
    if(finalPriceOverride && !ignorePriceOverride) {
      return finalPriceOverride.valueCents / 100;
    }

    // Default rounding logic if no override is found
    const roundingFactor = price < 100 ? 5 : 10;
    const roundedPrice = Math.round(price / roundingFactor) * roundingFactor;

    return roundedPrice;
  }

  getExtensionPrice(request: Request, onlyAmount = false, sync = false): Promise<[string | number, boolean]> {
    const parentRequest = this.cart.currentApplication?.parentRequest;
    const isOverstay = parentRequest ? this.isOverstay(parentRequest) : false;
    if(!request) return Promise.resolve([0, false]);
    const document = this.api.userInfo.server.paymentRequests.find(p => p.id === request.visaType.id);
    const daysRequested = this.getFieldValue(VisaFormName.PERSONAL_DETAILS, VisaFN.DAYS_REQUESTED, request.formConfig);

    if(daysRequested > 30) {
      this.loader.loading(true, {
        type: 'error',
        message: 'extension.error.maxDays',
        btnLabel: 'global.ok'
      });
      return Promise.resolve([0, false]);
    }

    const lastEntryEvent = this.getLastEntryEvent(parentRequest);

    const safePayment = parentRequest?.localUsageUntil
      ? {...parentRequest, localUsageUntil: new Date(parentRequest.localUsageUntil)}
      : parentRequest;

    const safeLastEntry = lastEntryEvent?.updatedOn
      ? {...lastEntryEvent, updatedOn: new Date(lastEntryEvent.updatedOn)}
      : lastEntryEvent;

    const baseExtensionPrice = Math.floor((eval(document.amountCentsDyn))({}, safePayment, safeLastEntry) / 100);

    if(isOverstay && (this.api.userRole.isWorker || this.api.userRole.isAdmin)) {
      const overstayPenalty = this.api.userInfo.server.paymentNonDocumentsCents.overstay / 100;
      const totalWithPenalty = baseExtensionPrice + overstayPenalty;
      if(this.api.userRole.isCustomer || this.api.userRole.isKiosk) {
        return Promise.resolve([(onlyAmount ? '' : document.currency + ' ') + totalWithPenalty, true]);
      } else {
        if(sync) return Promise.resolve([(onlyAmount ? '' : document.currency + ' ') + totalWithPenalty, false]);
        return new Promise((resolve) => {
          this.loader.loading(true, {
            type: 'warn',
            btnLabel: 'apply.overstay',
            custom: {
              closeBtnLabel: 'no_apply.overstay',
              icon: 'exclamation-triangle',
              innerHtml: this.lang.transform('paymentExtension.overstay', {
                basePrice: (onlyAmount ? '' : document.currency + ' ') + baseExtensionPrice,
                penalty: (onlyAmount ? '' : document.currency + ' ') + overstayPenalty,
                totalPrice: (onlyAmount ? '' : document.currency + ' ') + totalWithPenalty
              })
            }
          }).then((applyPenalty: boolean) => {
            if(applyPenalty) {
              const finalPrice = applyPenalty ? totalWithPenalty : baseExtensionPrice;
              resolve([(onlyAmount ? '' : document.currency + ' ') + finalPrice, applyPenalty]);
            } else {
              const finalizeNoFee = () => {
                const finalPrice = applyPenalty ? totalWithPenalty : baseExtensionPrice;
                resolve([(onlyAmount ? '' : document.currency + ' ') + finalPrice, applyPenalty]);
              };
              this.fingerprint.supervision(this.lang.transform('supervision.nofee')).then(() => {
                this.loader.loading(true, {type: 'info', btnLabel: 'continue', custom: {icon: 'camera', innerHtml: this.lang.transform('webcam.nofeeProof.confirm')}}).then((confirmed: boolean) => {
                  if(!confirmed) return;
                  const dialogRef = this.dialog.open(ScanDocWebcamComponent, {
                    panelClass: 'webcamCapturePanel',
                    disableClose: false,
                    data: {labelKey: 'NoFeeProof'}
                  });
                  dialogRef.afterClosed().subscribe((capturedImage: string | null) => {
                    if(capturedImage === null || capturedImage === undefined) return;
                    if(capturedImage) {
                      this.pendingNoFeeProof = capturedImage;
                    }
                    finalizeNoFee();
                  });
                });
              });
            }
          });
        });
      }
    }
    // For non-overstay cases, return base price
    const overstayPrice = isOverstay ? this.api.userInfo.server.paymentNonDocumentsCents.overstay / 100 : 0;
    const totalPrice = baseExtensionPrice + overstayPrice;
    return Promise.resolve([(onlyAmount ? '' : document.currency + ' ') + totalPrice, false]);
  }

  private payExtension(application?: FormApplication): Promise<{applyPenalty: boolean, paymentReference: string, paymentMode: string, paymentCurrency: string}> {
    return new Promise(resolve => this.getExtensionPrice(this.cart.currentApplication.requests[0], true).then(([priceResult, applyPenalty]) => {
      const handlePrice = (price: number) => {
        if(price > 0 && (this.api.userRole.isWorker || this.api.userRole.isAdmin) && (application || this.cart.currentApplication)?.isExtend) {
          const currencyInput = {
            formCtrlName: 'currency',
            type: 'select',
            label: 'form.currency',
            selectOptions: this.api.env.supportedCurrencies as any[],
            validator: [Validators.required]
          } as DialogFormInput;
          const dialogInput = [
            {
              formCtrlName: 'paymentReference',
              type: 'text',
              label: 'paymentReference.ask',
              maxLength: 60,
              value: uuid.v1()
            },
            currencyInput
          ] as DialogFormInput[];

          const cashTypeInput = {
            formCtrlName: 'cashType',
            type: 'select',
            label: 'method.payment',
            validator: [Validators.required],
            selectOptions: [
              {name: 'form.type.CASH', value: RequestCashType.CASH},
              {name: 'form.type.CARD', value: RequestCashType.CARD}
            ],
            onChangeAction: (newValue: RequestCashType) => {
              const list: any = newValue === RequestCashType.CARD ? (this.api.env.supportedCardCurrencies || []) : (this.api.env.supportedCurrencies || []);
              currencyInput.selectOptions = list;
            }
          };

          dialogInput.push(cashTypeInput as DialogFormInput);

          this.loader.loading(true, {type: 'valid', btnLabel: 'global.confirm', custom: {innerHtml: this.lang.transform('ask.isArrivalPaid') + this.getRatesText(application.requests[0].operationId, application.requests[0].nationality, price * 100), icon: 'money-bill-wave'}}).then(done => {
            if(done) {
              this.loader.loading(true, {type: 'question', message: 'ask.isExtension.confirm', btnLabel: 'yes', custom: {closeBtnLabel: 'no'}, dialogInput}).then((res: any) => {
                if(res && res.paymentReference && res.cashType && res.currency) {
                  this.inPrintProcess = true;
                  this.askPrint(res, price).then(() => {
                    this.inPrintProcess = false;
                    resolve({applyPenalty, paymentReference: res.paymentReference, paymentMode: res.cashType, paymentCurrency: res.currency});
                  });
                } else {
                  this.loader.loading(true, {type: 'error', message: 'error.payment.noPaymentReference'});
                }
              });
            }
          });
        } else {
          resolve({applyPenalty, paymentReference: '', paymentMode: '', paymentCurrency: ''});
        }
      };
      handlePrice(Number(priceResult));
    }));
  }

  askPrint(res: any, price: number): Promise<void> {
    return new Promise(resolve => {
      this.loader.loading(true);
      const receiptData: PrintReceiptData = {
        referencePrint: res.paymentReference,
        datePrint: new Date(),
        nameClient: (this.cart.currentApplication.currentPerson.firstName || '') + ' ' + (this.cart.currentApplication.currentPerson.lastName || ''),
        amount: price * 100,
        currency: res.currency,
        agentId: this.api.userInfo.id,
        typePayment: res.cashType,
        placeId: this.api.userPlaceId,
        buyDocument: this.cart.currentApplication.requests[0].visaType.id as DocumentType,
        idRequest: this.cart.currentApplication.requestId,
        operation: undefined,
        nationality: this.cart.currentApplication.currentPerson.nationality,
        gender: this.lang.transform('DYN.'+this.cart.currentApplication.currentPerson.sex),
        HQReference: this.cart.currentApplication.hqRef,
        vendorName: this.api.userInfo.server.emailConfig.copyrightName,
        vendorAddress: this.api.userInfo.server.emailConfig.address,
        vendorEmail: this.api.userInfo.server.emailConfig.email,
        serialNumber: undefined,
        qrCode: generateQrOffline(this.cart.currentApplication.requestId, this.cart.currentApplication.requests[0].timeslot_place_id, this.cart.currentApplication.externalId),
        current: this.cart.currentApplication.requests[0]
      };

      //Retrieve PrintService via the injector to avoid circular dependency
      const printService = this.injector.get(PrintService);
      printService.print<PrintReceiptData>(DocsPrintType.RECEIPT, receiptData);

      this.printSubscription = printService.printLoaded.subscribe({
        next: (loaded: AllDocumentsPrintType) => {
          if(loaded) {
            this.loader.loading(true, {
              type: 'info',
              message: 'payment.ask.print',
              btnLabel: 'global.yes',
              custom: {closeBtnLabel: 'global.no.retry'}
            }).then((done) => {
              this.printSubscription?.unsubscribe();
              if(done) {
                resolve();
              } else {
                // Retry
                return this.askPrint(res, price).then(resolve);
              }
            });
          }
        }
      });
    });
  }

  cleanupDraft(paymentId: string) {
    this.allMyPayments = this.allMyPayments.filter((payment) => payment.id !== paymentId);
    this.allMyApplications = this.allMyApplications.map((app) => ({
      ...app,
      requests: app.requests.filter((request) => request.id !== paymentId)
    })).filter((app) => app.requests.length > 0);
    if(this.allMyApplications.length === 0) {
      this.allBatchIds = [];
    }
  }

  cleanupExtension(extensionId: string) {
    this.allMyApplications = this.allMyApplications.map((app) => ({
      ...app,
      requests: app.requests.map((request) => ({
        ...request,
        extensions: request.extensions?.filter((extension) => extension.id !== extensionId)
      }))
    }));
    this.allExtensions = this.allExtensions.filter((extension) => extension.id !== extensionId);
    this.allMyPayments = this.allMyPayments.filter((payment) => payment.id !== extensionId);
  }

  cleanupError(isExtend = false, reason?: string) {
    if(isExtend) {
      const idToCancel = this.cart.currentApplication.requestId;
      const relatedApplication = this.allMyApplications.find(app => app.requests.some(request => request.id === this.cart.currentApplication.requestId));
      const relatedPayment = relatedApplication?.requests.find(request => request.id === idToCancel);
      const extensionsDraft = relatedPayment?.extensions?.filter(ext => ext.state === RequestState.PROPOSED);
      const cancelPromises = extensionsDraft.map(ext => this.api.cancelPayment(ext.id, reason || 'Cleanup of extension draft'));
      return Promise.all(cancelPromises).then(() => {
        extensionsDraft.forEach(ext => {
          this.cleanupExtension(ext.id);
        });
      });
    } else {
      const existingPayments = this?.allMyPayments.filter(payment => payment.batchId === this.cart.currentApplication.travelName);
      const existingDraftPayment = existingPayments?.filter(payment => payment.state === RequestState.PROPOSED);
      if(existingDraftPayment.length) {
        this.loader.loading(true);
        const cancelPaymentPromises = existingDraftPayment.map((payment) => this.api.cancelPayment(payment.id, reason || 'Cleanup of draft payment'));
        return Promise.all(cancelPaymentPromises).then(() => {
          existingDraftPayment.forEach(payment => {
            this.cleanupDraft(payment.id);
          });
          this.cart.currentApplication.travelName = this.cart.getUniqueReference(this.allMyPayments?.map(p => p.batchId) || []);
          return this.cart.saveCurrentApplication();
        }).finally(() => this.loader.loading(false));
      } else {
        return Promise.resolve();
      }
    }
  }

  public getStateForInitialRevisions(request: RequestWithDuplicata): string {
    const statusKey = this.isExtension(request) ? 'payment_status.extension.DYN.' : 'payment_status.DYN.';
    if(request.state === RequestState.DELAY_PAY_NOT_CONFIRMED) {
      return this.lang.transform('zwevisaExtC.finalReview.state');
    } else if(documentTypeIsVisaExtC(request.operationId)) {
      return request.subState === RequestSubState.REVISIONS_1
        ? this.lang.transform('zwevisaExtC.finalReview.state')
        : this.lang.transform('zwevisaExtC.review.state');
    } else {
      const reviewSuffix = this.api.userRole.isCustomer ? '' : '.review';
      return this.lang.transform(statusKey + RequestState.PAID_NOT_CONFIRMED + reviewSuffix,
        {reviewCount: request.subState});
    }
  }

  public getDescStateForInitialRevisions(request: RequestWithDuplicata, isRevisionSinceOneWeek: boolean): string {
    let descState: string;

    if(request.state === RequestState.DELAY_PAY_NOT_CONFIRMED) {
      descState = this.lang.transform('onlyFinalReview');
    } else if(documentTypeIsVisaExtC(request.operationId)) {
      descState = request.subState === RequestSubState.REVISIONS_1
        ? this.lang.transform('zwevisaExtC.finalReview.desc')
        : this.lang.transform('zwevisaExtC.review.desc');
    } else {
      const reviewSuffix = this.api.userRole.isCustomer ? '' : '.review';
      descState = this.lang.transform('payment_status.desc.DYN.' + RequestState.PAID_NOT_CONFIRMED + reviewSuffix,
        {reviewCount: request.subState});
    }
    return descState + (isRevisionSinceOneWeek ? ' ' + this.lang.transform('sinceOneWeek') : '');
  }

  public getStateForFinalRevision(request: RequestWithDuplicata): string {
    const statusKey = this.isExtension(request) ? 'payment_status.extension.DYN.' : 'payment_status.DYN.';
    const finalReviewSuffix = (this.api.userRole.isCustomer ? '' : '.finalReview');

    return this.lang.transform(statusKey + RequestState.PAID_NOT_CONFIRMED + finalReviewSuffix);
  }

  public getDescStateForFinalRevision(request: RequestWithDuplicata, isRevisionSinceOneWeek: boolean): string {
    let descState: string;

    if(documentTypeIsVisaExtAB(request.operationId)) {
      descState = this.lang.transform('final.ZWEVISAEXTAB');
    } else {
      const finalReviewSuffix = (this.api.userRole.isCustomer ? '' : '.finalReview');

      descState = this.lang.transform('payment_status.desc.DYN.' + RequestState.PAID_NOT_CONFIRMED + finalReviewSuffix);
    }
    return descState + (isRevisionSinceOneWeek ? ' ' + this.lang.transform('sinceOneWeek') : '');
  }

  public getDelayPayState(request: RequestWithDuplicata): string {
    if(this.isExtensionOperation(request.operationId)) {
      return this.lang.transform('zwevisaExtValid');
    }
    const operationSuffix = this.getOperationSuffix(request.operationId);
    return this.lang.transform(`payment_status.DYN.${RequestState.DELAY_PAY}${operationSuffix}`);
  }

  public getDelayPayDescState(request: RequestWithDuplicata): string {
    if(this.isExtensionOperation(request.operationId)) {
      return this.lang.transform('zwevisaExtValid.desc');
    }

    const operationSuffix = this.getOperationSuffix(request.operationId);
    return this.lang.transform(`payment_status.desc.DYN.${RequestState.DELAY_PAY}${operationSuffix}`);
  }

  private isExtensionOperation(operationId: DocumentType): boolean {
    return documentTypeIsVisaExtAB(operationId) || documentTypeIsVisaExtC(operationId);
  }

  private getOperationSuffix(operationId: DocumentType): string {
    switch(operationId) {
    case getDocumentTypeIsExit():
      return '.exit';
    default:
      return '';
    }
  }

  /**
   * Checks if a request is a transit request
   * @param request - The request to check for transit status
   * @returns boolean indicating if the request is a transit request
   */
  isTransit(request: RequestWithDuplicata | Request): boolean {
    return request?.internalIndexedData?.indexOf('transit') > -1;
  }

  isResidentPermit(requestWithDuplicata: RequestWithDuplicata, request?: Request): boolean {
    const passportType = requestWithDuplicata?.metadata?.PassportType || request?.passportType;
    return (passportType === PassportType.RESIDENT_PERMIT || passportType === PassportType.PERMANENT_RESIDENT_PERMIT);
  }

  isPermitNumberCheckRequired(request: RequestWithDuplicata): boolean {
    if(!('permitResponse' in request)) { // If permitResponse key doesn't exist at all, check is not required
      return false;
    }
    return (request.permitResponse === null || request.permitResponse.is_valid === false) && this.isResidentPermit(request); // If permitResponse is null or invalid, check is required
  }

  private getLastEntryEvent(request: RequestWithDuplicata): PlatformEvent | null {
    if(!request?.events) return null;

    const entryEvents = request.events
      .filter(ev => ev.consumeData?.Operation === 'IN')
      .sort((a, b) => new Date(b.updatedOn).getTime() - new Date(a.updatedOn).getTime());

    return entryEvents[0];
  }
}
